import {
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  useDisclosure,
  useMergeRefs,
  useBoolean,
  FormErrorMessage,
  FormHelperText,
  FormControlProps,
  InputProps,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverTrigger,
  PopoverArrow,
} from '@chakra-ui/react';
import React, { forwardRef } from 'react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

export interface PasswordFieldProps extends FormControlProps {
  id: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  placeholder?: string;
  inputProps?: InputProps;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ id, label, helperText, errorText, inputProps, ...props }) => {
    const { isOpen, onToggle } = useDisclosure();
    const [isFocused, setFocus] = useBoolean();
    const [isHighlight, setHighlight] = useBoolean();
    const [capsLock, setCapsLock] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const onClickReveal = () => {
      onToggle();
      const input = inputRef.current;

      if (input) {
        input.focus({
          preventScroll: true,
        });
        const length = input.value.length * 2;
        requestAnimationFrame(() => {
          input.setSelectionRange(length, length);
        });
      }
    };

    const detectCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.getModifierState('CapsLock')) {
        setCapsLock(true);
        return;
      }
      setCapsLock(false);
    };

    return (
      <FormControl id={id} {...props}>
        <Flex justify="space-between">{label && <FormLabel mb={1}>{label}</FormLabel>}</Flex>
        <InputGroup
          border={'light'}
          borderRadius={'base'}
          boxSizing={'border-box'}
          onFocus={setFocus.on}
          onBlur={setFocus.off}
          size={inputProps?.size}
          h={(inputProps?.size ?? 'sm') === 'md' ? 12 : 8}
          borderColor={isFocused ? 'notification' : props.isInvalid ? 'error' : 'border.base'}
        >
          <Popover autoFocus={false} placement={'bottom-start'} arrowPadding={50} isOpen={capsLock}>
            <PopoverTrigger>
              <Input
                ref={inputRef}
                name="password"
                type={isOpen ? 'text' : 'password'}
                placeholder={props.placeholder}
                border={'none'}
                onKeyDown={(e) => detectCapsLock(e)}
                h="100%"
                {...inputProps}
              />
            </PopoverTrigger>
            <PopoverContent color="black" bg="#F5F5F5" width={'fit-content'}>
              <PopoverArrow bg={'#F5F5F5'} />
              <PopoverBody fontSize={'sm'}>Caps lock is on</PopoverBody>
            </PopoverContent>
          </Popover>
          <InputRightAddon h="100%" border={'none'}>
            <IconButton
              bg="transparent !important"
              variant="ghost"
              size={inputProps?.size}
              aria-label={isOpen ? 'Mask password' : 'Reveal password'}
              onFocus={setHighlight.on}
              onBlur={setHighlight.off}
              icon={
                isOpen ? (
                  <ViewIcon fontSize={inputProps?.size} color={isHighlight ? 'primary.dark' : 'primary.base'} />
                ) : (
                  <ViewOffIcon fontSize={inputProps?.size} color={isHighlight ? 'primary.dark' : 'primary.base'} />
                )
              }
              onClick={onClickReveal}
            />
          </InputRightAddon>
        </InputGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        <FormErrorMessage>{errorText}</FormErrorMessage>
      </FormControl>
    );
  },
);
